import * as PropTypes from 'prop-types';
import React from 'react';

import {ChemDrawStoichiometryData, ChemDrawStoichiometryResult} from 'chemistry/ChemistryConstants';
import DataConnector from 'connect/DataConnector';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {Table} from 'notebook/tables/TableConstants';

interface ChemDrawStoichiometryTableProps {
  noteLinkedObjectId: string;
  noteLinkedObjectCid: string;
  onSelectTable: () => void;
  indentation: number;
}

interface EnhancedProps {
  chemDrawStoichiometryData: ChemDrawStoichiometryData;
}

class ChemDrawStoichiometryTableConnector extends DataConnector<
  ChemDrawStoichiometryTableProps,
  EnhancedProps
> {
  enhanceProps(): EnhancedProps {
    return {
      chemDrawStoichiometryData: this.getOrFetchRequired(
        ManagedModelType.CHEM_DRAW_STOICHIOMETRY_DATA,
        this.props.noteLinkedObjectId
      ),
    };
  }
}

class ChemDrawStoichiometryTable extends React.Component<ChemDrawStoichiometryTableProps & EnhancedProps> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    noteLinkedObjectId: PropTypes.string.isRequired,
    noteLinkedObjectCid: PropTypes.string.isRequired,
    onSelectTable: PropTypes.func.isRequired,
    indentation: PropTypes.number.isRequired,
    // chemDrawStoichiometryData provided by connect, and omitted because it is never specified in JS.
  };

  _createTable(tableName: string, header: Array<string>, rows: Array<Array<string>>): Table {
    return {
      name: tableName,
      table: rows.map((row) => row.map((text) => ({text}))),
      widths: header.map(() => 120),
      columnConstraints: [null],
      headerRow: false,
      headerCol: false,
      columnLabels: header,
    };
  }

  _renderStoichiometryTable(tableType: string, table: Table): JSX.Element {
    // TODO(BNCH-5877): Make the defaultProps come through the wrapped component
    return (
      <div className="chemDrawStoichiometryTable">
        <SimpleTableEditable
          {...SimpleTableEditable.defaultProps}
          {...standaloneProps}
          table={table}
          cid={`chem-draw-stoichiometry-${tableType}-table-${this.props.noteLinkedObjectCid}`}
          hideFormulaBox={true}
          indentation={this.props.indentation}
          isCompactBottomSpacing={true}
          isTableNameUsed={() => false}
          locked={true}
          selectTable={this.props.onSelectTable}
          viewer={true}
        />
      </div>
    );
  }

  render(): JSX.Element {
    const {chemDrawStoichiometryData} = this.props;
    if (chemDrawStoichiometryData.result === ChemDrawStoichiometryResult.SUCCESS) {
      return (
        <div className="chemDrawStoichiometryTable-group">
          {this._renderStoichiometryTable(
            'reactants',
            this._createTable(
              'Reactants',
              chemDrawStoichiometryData.reactantsHeader,
              chemDrawStoichiometryData.reactantsTable
            )
          )}
          {this._renderStoichiometryTable(
            'products',
            this._createTable(
              'Products',
              chemDrawStoichiometryData.productsHeader,
              chemDrawStoichiometryData.productsTable
            )
          )}
        </div>
      );
    } else if (chemDrawStoichiometryData.result === ChemDrawStoichiometryResult.ERROR) {
      return (
        <div className="chemDrawStoichiometryTable-group u-danger-color">
          Cannot display stoichiometry: {chemDrawStoichiometryData.message}
        </div>
      );
    } else {
      return <span />;
    }
  }
}

export default ChemDrawStoichiometryTableConnector.connect(ChemDrawStoichiometryTable);
